import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { fetchChatAttachment } from "../../api/ProcurementClient";
import { ChatAttachment } from "../../models/Chat";
import { base64ToBlob, formatFileSize } from "./chatUtils";

interface ChatAttachmentDialogProps {
  attachment: ChatAttachment | null;
  onClose: () => void;
}

const ChatAttachmentDialog: React.FC<ChatAttachmentDialogProps> = ({ attachment, onClose }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attachment) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setError(null);
    fetchChatAttachment(attachment.id)
      .then((content) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(base64ToBlob(content.contentBase64, content.contentType));
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load file");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment]);

  const type = attachment?.contentType ?? "";
  const isImage = type.startsWith("image/");
  const isPdf = type === "application/pdf" || /\.pdf$/i.test(attachment?.fileName ?? "");
  const isText = type.startsWith("text/");

  const download = () => {
    if (!url || !attachment) return;
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = attachment.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <Dialog
      open={Boolean(attachment)}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: { height: isImage || isPdf || isText ? "90vh" : undefined } } }}
    >
      <DialogTitle sx={{ pr: 2 }}>
        <Typography variant="subtitle1" noWrap fontWeight={600}>
          {attachment?.fileName}
        </Typography>
        {attachment && (
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(attachment.sizeBytes)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", p: 0, minHeight: 160 }}>
        {error ? (
          <Alert severity="error" sx={{ m: 2, flex: 1 }}>
            {error}
          </Alert>
        ) : !url ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : isImage ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.900",
            }}
          >
            <Box
              component="img"
              src={url}
              alt={attachment?.fileName}
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </Box>
        ) : isPdf || isText ? (
          <Box component="iframe" src={url} title={attachment?.fileName} sx={{ flex: 1, border: 0 }} />
        ) : (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
            <Typography color="text.secondary">
              No preview for this file type. Download it to open.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Download />} onClick={download} disabled={!url}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatAttachmentDialog;
